import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { CreateFeedbackIssueRequest, FeedbackIssueSeverity, FeedbackIssueType, User } from '@kangaroo/shared';

import { submitFeedbackIssue } from '../../features/feedback';

type FeedbackDialogProps = {
  open: boolean;
  onClose: () => void;
  user: User;
  currentUrl: string;
  onSubmitted?: () => void;
};

const ISSUE_TYPES: Array<{ value: FeedbackIssueType; label: string }> = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature' },
  { value: 'task', label: 'Task' },
  { value: 'question', label: 'Question' },
];

const SEVERITIES: Array<{ value: FeedbackIssueSeverity; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

function blankForm(): CreateFeedbackIssueRequest {
  return {
    type: 'bug',
    title: '',
    severity: 'medium',
    area: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    notes: '',
    currentUrl: '',
    browser: '',
  };
}

function fieldOrPlaceholder(value: string | null | undefined) {
  return value?.trim() || '_Not provided_';
}

function getErrorMessage(error: unknown, fallback = 'Could not submit feedback.') {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) {
      return response.data.message;
    }
  }

  if (error instanceof Error && error.message && !error.message.startsWith('Request failed with status code')) {
    return error.message;
  }

  return fallback;
}

function buildPreview(form: CreateFeedbackIssueRequest, user: User) {
  return [
    `# ${form.title.trim() || 'Feedback title'}`,
    '',
    '## Metadata',
    `- Type: ${form.type}`,
    `- Severity: ${form.severity ?? 'medium'}`,
    `- Area: ${fieldOrPlaceholder(form.area)}`,
    `- URL: ${fieldOrPlaceholder(form.currentUrl)}`,
    '',
    '## Reporter',
    `- Name: ${user.name}`,
    `- Email: ${user.email}`,
    '',
    `## Steps To Reproduce\n${fieldOrPlaceholder(form.stepsToReproduce)}`,
    '',
    `## Expected Behavior\n${fieldOrPlaceholder(form.expectedBehavior)}`,
    '',
    `## Actual Behavior\n${fieldOrPlaceholder(form.actualBehavior)}`,
    '',
    `## Notes\n${fieldOrPlaceholder(form.notes)}`,
  ].join('\n');
}

export function FeedbackDialog({ open, onClose, user, currentUrl, onSubmitted }: FeedbackDialogProps) {
  const [form, setForm] = useState<CreateFeedbackIssueRequest>(() => blankForm());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const preview = useMemo(() => buildPreview(form, user), [form, user]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm({
      ...blankForm(),
      currentUrl,
      browser: window.navigator.userAgent,
    });
    setError(null);
    setSubmitted(false);
    setAttachments([]);
    setPreviewOpen(false);
  }, [currentUrl, open]);

  function updateField<Key extends keyof CreateFeedbackIssueRequest>(key: Key, value: CreateFeedbackIssueRequest[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit() {
    if (!form.title.trim()) {
      setError('Add a title first.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await submitFeedbackIssue({
        ...form,
        title: form.title.trim(),
      }, attachments);
      setSubmitted(true);
      onSubmitted?.();
    } catch (caught) {
      setError(getErrorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>Send feedback</DialogTitle>
      <DialogContent>
        <Stack spacing={1.4} sx={{ pt: 0.5 }}>
          {submitted ? (
            <Alert severity="success">Feedback sent. It was saved for GitHub sync.</Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
            <TextField
              select
              label="Type"
              value={form.type}
              onChange={(event) => updateField('type', event.target.value as FeedbackIssueType)}
              sx={{ minWidth: 150 }}
            >
              {ISSUE_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Severity"
              value={form.severity ?? 'medium'}
              onChange={(event) => updateField('severity', event.target.value as FeedbackIssueSeverity)}
              sx={{ minWidth: 150 }}
            >
              {SEVERITIES.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Area"
              value={form.area ?? ''}
              onChange={(event) => updateField('area', event.target.value)}
              placeholder="Sessions, uploads, billing"
              fullWidth
            />
          </Stack>
          <TextField
            label="Title"
            value={form.title}
            onChange={(event) => updateField('title', event.target.value)}
            placeholder="Short issue title"
            required
            fullWidth
          />
          <TextField
            label="Steps to reproduce"
            value={form.stepsToReproduce ?? ''}
            onChange={(event) => updateField('stepsToReproduce', event.target.value)}
            multiline
            minRows={3}
            fullWidth
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2}>
            <TextField
              label="Expected behavior"
              value={form.expectedBehavior ?? ''}
              onChange={(event) => updateField('expectedBehavior', event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <TextField
              label="Actual behavior"
              value={form.actualBehavior ?? ''}
              onChange={(event) => updateField('actualBehavior', event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
          <TextField
            label="Notes"
            value={form.notes ?? ''}
            onChange={(event) => updateField('notes', event.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Stack spacing={0.8}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              Add screenshots or videos
              <input
                hidden
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={(event) => {
                  const files = [...(event.target.files ?? [])].slice(0, 4);
                  setAttachments(files);
                  event.target.value = '';
                }}
              />
            </Button>
            {attachments.length > 0 ? (
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {attachments.map((file) => (
                  <Chip
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    label={`${file.name} (${Math.max(1, Math.round(file.size / 1024 / 1024))} MB)`}
                    onDelete={() => setAttachments((current) => current.filter((entry) => entry !== file))}
                    size="small"
                  />
                ))}
              </Stack>
            ) : null}
          </Stack>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'rgba(15,23,42,0.42)',
              p: 1.2,
            }}
          >
            <Button
              size="small"
              onClick={() => setPreviewOpen((value) => !value)}
              sx={{ px: 0, minWidth: 0, fontSize: '0.72rem', fontWeight: 850, color: 'text.secondary' }}
            >
              {previewOpen ? 'Hide markdown preview' : 'Show markdown preview'}
            </Button>
            <Collapse in={previewOpen} timeout="auto">
              <Box
                component="pre"
                sx={{
                  mt: 0.8,
                  mb: 0,
                  fontSize: '0.72rem',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  color: 'text.primary',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  maxHeight: 220,
                  overflow: 'auto',
                }}
              >
                {preview}
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Close</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting || submitted || !form.title.trim()}>
          Send feedback
        </Button>
      </DialogActions>
    </Dialog>
  );
}
